'use client';

import { useState, useEffect } from 'react';
import { Box, CircularProgress, Grid, TextField, Stack, Button, InputAdornment } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import FormDialog from '../../../components/FormDialog';
import PickerField from '../../../components/PickerField';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';
import usePickerPagination from '../../../hooks/usePickerPagination';

import { useCompanyDataList } from '../../../stores/companyStore';
import { useFuelTypeList } from '../../../stores/fuelTypeStore';
import {
  getMasterFuelPriceById,
  createMasterFuelPrice,
  updateMasterFuelPrice,
  revalidateMasterFuelPriceList
} from '../../../stores/masterFuelPriceStore';
import MasterCompanyAddEdit from '../company/MasterCompanyAddEdit';
// import MasterFuelTypeAddEdit from '../fuel-type/MasterFuelTypeAddEdit';
import { formatRupiahInput, parseRupiahInput } from '../../../utils/numberFormatter';
const MasterFuelPriceAddEdit = ({ open, id, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const notify = useNotifier();
  const [loading, setLoading] = useState(false);

  const [initialValues, setInitialValues] = useState({
    company: '',
    fuel_type: '',
    fuel_type_name: '',
    fuel_type_code: '',
    fuel_type_category: '',
    fuel_type_desc: '',
    fuel_type_uom: '',
    price: '',
    description: '',
    valid_at: dayjs()
  });

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const companyOptions = companyPickerParams.options;

  const uniqueByValue = (arr = []) => {
    const map = new Map();
    (arr || []).forEach((item) => {
      const key = String(item.value ?? item.id ?? '');
      if (key && !map.has(key)) map.set(key, item);
    });
    return Array.from(map.values());
  };

  const fuelTypePickerParams = usePickerPagination({
    fetchHook: useFuelTypeList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchValue],
    searchKey: 'name',
    formatOption: (item) => {
      const name = item.name || '';
      const code = item.code || '';
      const id = item.id != null ? item.id : '';
      let label = name;
      if (code) label += ` - ${code}`;
      if (id) label += ` - ${id}`;
      return {
        value: item.id,
        label: label || `Fuel ${item.id}`,
        original: item
      };
    }
  });

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetchInitial = async () => {
      setLoading(true);
      try {
        if (id) {
          const data = await getMasterFuelPriceById(id);
          if (!mounted) return;
          if (data) {
            setInitialValues({
              company: data.company ?? data.company_id ?? '',
              fuel_type: data.fuel_type ?? data.fuel_type_id ?? '',
              fuel_type_name: data.fuel_type_name ?? data?.fuel_type_join?.name ?? '',
              fuel_type_code: data.fuel_type_code ?? data?.fuel_type_join?.code ?? '',
              fuel_type_category: data.fuel_type_category ?? data?.fuel_type_join?.category ?? '',
              fuel_type_desc: data.fuel_type_desc ?? data?.fuel_type_join?.description ?? '',
              fuel_type_uom: data.fuel_type_uom ?? data?.fuel_type_join?.uom_type ?? '',
              price: data.price ?? '',
              description: data.description ?? '',
              valid_at: data.valid_at ? dayjs(data.valid_at) : dayjs()
            });
          }
        } else {
          let defaultCompany = '';
          const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
          if (stored && stored !== 'all') {
            defaultCompany = String(stored);
          }
          setInitialValues({
            company: defaultCompany,
            fuel_type: '',
            fuel_type_name: '',
            fuel_type_code: '',
            fuel_type_category: '',
            fuel_type_desc: '',
            fuel_type_uom: '',
            price: '',
            description: '',
            valid_at: dayjs()
          });
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        notify({ message: t('notify.failedLoadData', 'Failed to load data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchInitial();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, open]);

  const validationSchema = Yup.object().shape({
    company: Yup.mixed().required(t('validation.companyRequired', 'Company is required')),
    fuel_type: Yup.mixed().required(t('validation.fuelTypeRequired', 'Fuel Type is required')),
    price: Yup.number()
      .typeError(t('validation.priceRequired', 'Price is required'))
      .required(t('validation.priceRequired', 'Price is required'))
      .moreThan(0, 'Must be greater than 0'),
    valid_at: Yup.date()
      .required(t('validation.dateRequired', 'Valid At date is required'))
      .min(dayjs().startOf('day').toDate(), 'Must be today or future date')
  });

  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          company: Number(values.company),
          fuel_type: Number(values.fuel_type),
          price: Number(values.price),
          description: values.description ?? '',
          valid_at: dayjs(values.valid_at).format('YYYY-MM-DDT00:00:00Z'),
          fuel_type_name: values.fuel_type_name,
          fuel_type_code: values.fuel_type_code,
          fuel_type_category: values.fuel_type_category,
          fuel_type_desc: values.fuel_type_desc,
          fuel_type_uom: values.fuel_type_uom
        };

        let response;
        if (id) {
          response = await updateMasterFuelPrice(id, payload);
          notify({ message: t('notify.update', 'Updated successfully'), severity: 'success' });
        } else {
          response = await createMasterFuelPrice(payload);
          notify({ message: t('notify.create', 'Created successfully'), severity: 'success' });
        }

        if (response?.code === 400 || response?.status === false || response?.error) {
          throw response;
        }

        revalidateMasterFuelPriceList();
        formik.resetForm();
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const selectedFuelTypeOption = formik.values.fuel_type
    ? [
        {
          value: formik.values.fuel_type,
          label: formik.values.fuel_type_code
            ? `${formik.values.fuel_type_name} - ${formik.values.fuel_type_code}`
            : formik.values.fuel_type_name || `Fuel ${formik.values.fuel_type}`,
          original: {
            id: formik.values.fuel_type,
            name: formik.values.fuel_type_name,
            code: formik.values.fuel_type_code,
            category: formik.values.fuel_type_category,
            description: formik.values.fuel_type_desc,
            uom_type: formik.values.fuel_type_uom
          }
        }
      ]
    : [];

  const fuelTypeOptions = uniqueByValue([...selectedFuelTypeOption, ...fuelTypePickerParams.options]);

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{
          textTransform: 'none',
          borderRadius: 1,
          px: 3,
          fontWeight: 700,
          boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
        }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormDialog
        open={open}
        onClose={handleClose}
        title={id ? t('general.edit', 'Edit') + ' Master Fuel Price' : t('general.add', 'Add') + ' Master Fuel Price'}
        maxWidth="md"
        fullHeight={false}
        actions={dialogActions}
      >
        <Box sx={{ width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item size={{ xs: 12, md: 6 }}>
                  <Stack spacing={2}>
                    <PickerField
                      label={t('general.company', 'Company') + ' *'}
                      value={formik.values.company}
                      {...companyPickerParams}
                      options={companyOptions}
                      onChange={(opt) => {
                        const val = typeof opt === 'object' ? opt?.value : opt;
                        const selectedId = val ?? null;
                        formik.setFieldValue('company', selectedId);
                      }}
                      dialogTitle={t('masterCountry.selectCompany', 'Select Company')}
                      error={formik.touched.company && !!formik.errors.company}
                      helperText={formik.touched.company && formik.errors.company}
                      allowAdd
                      AddComponent={MasterCompanyAddEdit}
                    />

                    <PickerField
                      label={'Fuel Type *'}
                      value={formik.values.fuel_type}
                      {...fuelTypePickerParams}
                      options={fuelTypeOptions}
                      onChange={(opt) => {
                        const val = typeof opt === 'object' ? opt?.value : opt;
                        const selectedId = val ?? null;
                        formik.setFieldValue('fuel_type', selectedId);

                        // Auto-fill other fields based on selected fuel type
                        const selectedObj =
                          typeof opt === 'object' ? opt?.original : fuelTypeOptions.find((o) => o.value === selectedId)?.original;
                        if (selectedObj) {
                          formik.setFieldValue('fuel_type_name', selectedObj.name || '');
                          formik.setFieldValue('fuel_type_code', selectedObj.code || '');
                          formik.setFieldValue('fuel_type_category', selectedObj.category || '');
                          formik.setFieldValue('fuel_type_desc', selectedObj.description || '');
                          formik.setFieldValue('fuel_type_uom', selectedObj.uom_type || '');
                        } else {
                          formik.setFieldValue('fuel_type_name', '');
                          formik.setFieldValue('fuel_type_code', '');
                          formik.setFieldValue('fuel_type_category', '');
                          formik.setFieldValue('fuel_type_desc', '');
                          formik.setFieldValue('fuel_type_uom', '');
                        }
                      }}
                      dialogTitle={'Select Fuel Type'}
                      error={formik.touched.fuel_type && !!formik.errors.fuel_type}
                      helperText={formik.touched.fuel_type && formik.errors.fuel_type}
                      infoMessage="If the required fuel type is not listed here, please contact your System Administrator to request a new entry."
                    />
                    <DatePicker
                      label="Valid At *"
                      value={formik.values.valid_at}
                      onChange={(newValue) => {
                        formik.setFieldValue('valid_at', newValue);
                      }}
                      minDate={dayjs()}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'medium',
                          error: formik.touched.valid_at && !!formik.errors.valid_at,
                          helperText: formik.touched.valid_at && formik.errors.valid_at
                        }
                      }}
                    />

                    <TextField
                      fullWidth
                      size="medium"
                      id="price"
                      name="price"
                      label="Price (IDR) *"
                      type="text"
                      value={formatRupiahInput(formik.values.price)}
                      onChange={(e) => {
                        const parsed = parseRupiahInput(e.target.value);
                        formik.setFieldValue('price', parsed);
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.price && !!formik.errors.price}
                      helperText={formik.touched.price && formik.errors.price}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">Rp</InputAdornment>
                      }}
                    />

                    <TextField
                      fullWidth
                      size="medium"
                      id="description"
                      name="description"
                      label={t('general.description', 'Description')}
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.description && !!formik.errors.description}
                      helperText={formik.touched.description && formik.errors.description}
                      multiline
                      rows={2}
                    />
                  </Stack>
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }}>
                  <Stack spacing={2}>
                    <TextField fullWidth size="medium" label="Fuel Type Name" value={formik.values.fuel_type_name} disabled />
                    <TextField fullWidth size="medium" label="Fuel Type Code" value={formik.values.fuel_type_code} disabled />
                    <TextField fullWidth size="medium" label="Fuel Type Category" value={formik.values.fuel_type_category} disabled />
                    <TextField fullWidth size="medium" label="Fuel Type UOM" value={formik.values.fuel_type_uom} disabled />
                    <TextField
                      fullWidth
                      size="medium"
                      label="Fuel Type Description"
                      value={formik.values.fuel_type_desc}
                      disabled
                      multiline
                      rows={2}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </form>
          )}
        </Box>
      </FormDialog>
    </LocalizationProvider>
  );
};

export default MasterFuelPriceAddEdit;
